<template>
    <div
        class="text-viewing"
        ref="root"
        :class="[
            `vs-input-${color}`,
            {
                'isFocus': isFocus,
                'input-icon-validate-success': success,
                'input-icon-validate-danger': danger || hasValidationErrors,
                'input-icon-validate-warning': warning,
                'is-label-placeholder': labelPlaceholder,
                'with-label': labelPlaceholder ? false : label,
                'no-icon': !icon
            }
        ]"
    >
        <label
            v-if="label && !labelPlaceholder"
            class="vs-input--label"
            :for="`${_uid}-textarea`"
            @click="focusInput"
            v-html="label_text"
            :class="[{ 'pointer-events-none': disabled }]"
        ></label>
        <div
            class="vs-textarea-primary vs-con-textarea h-full"
            :class="[{ 'vs-con-textarea-danger': danger || hasValidationErrors }]"
        >
            <textarea
                v-validate="`${validationRule}`"
                ref="vsinput"
                :id="`${_uid}-textarea`"
                :name="name"
                style=""
                :autofocus="autofocus"
                :value="value"
                v-bind="$attrs"
                :readonly="disabled"
                v-on="listeners"
                class="vx-textarea--input h-full"
                :class="[
                    size,
                    {
                        'hasValue': value,
                        'hasIcon': icon,
                        'icon-after-input': iconAfter
                    }
                ]"
                :placeholder="null"
            >
            </textarea>

            <transition name="placeholderx">
                <span
                    v-if="isValue && placeholder_text"
                    ref="spanplaceholder"
                    :style="styleLabel"
                    :class="[
                        labelPlaceholder && size,
                        size,
                        {
                            'translate': value || isFocus,
                            'vs-placeholder-label': labelPlaceholder
                        }
                    ]"
                    class="input-span-placeholder vs-input--placeholder"
                    @click="focusInput"
                    v-html="placeholder_text"
                >
                </span>
            </transition>
            <i v-if="icon" class="vs-input--icon vx-icon" :class="[icon, iconPack]"></i>
        </div>

        <div :class="{ 'validation-padding': !removeValidationPadding }">
            <transition-group @before-enter="beforeEnter" @enter="enter" @leave="leave">
                <div
                    v-if="success"
                    key="success"
                    class="con-text-validation vs-input--text-validation"
                >
                    <span
                        class="span-text-validation span-text-validation-success vs-input--text-validation-span"
                    >
                        {{ successText }}
                    </span>
                </div>
                <div
                    v-else-if="danger"
                    key="danger"
                    class="con-text-validation span-text-validation-danger vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ dangerText }}
                    </span>
                </div>
                <div
                    v-else-if="hasValidationErrors"
                    key="error"
                    class="con-text-validation span-text-validation-danger vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ errorText }}
                    </span>
                </div>
                <div
                    v-else-if="warning"
                    key="warning"
                    class="con-text-validation span-text-validation-warning vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ warningText }}
                    </span>
                </div>

                <div
                    v-if="descriptionText"
                    key="description"
                    class="con-text-validation span-text-validation vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ descriptionText }}
                    </span>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import validationMixins from '@/plugins/validation/mixins.js'
import _color from '@/assets/utils/color.js'

export default {
    name: 'text-viewing',
    mixins: [validationMixins],
    props: {
        value: {
            default: '',
            type: [String, Number]
        },
        labelPlaceholder: {
            default: null,
            type: [String, Number]
        },
        placeholder: {
            default: null,
            type: [String, Number]
        },
        label: {
            default: null,
            type: [String, Number]
        },
        autofocus: {
            default: false,
            type: [Boolean, String]
        },
        icon: {
            default: null,
            type: String
        },
        iconAfter: {
            default: false,
            type: [Boolean, String]
        },
        iconNoBorder: {
            default: false,
            type: Boolean
        },
        iconPack: {
            default: 'feather',
            type: String
        },
        color: {
            default: 'primary',
            type: String
        },
        disabled: {
            default: false,
            type: Boolean
        },
        success: {
            default: false,
            type: Boolean
        },
        danger: {
            default: false,
            type: Boolean
        },
        warning: {
            default: false,
            type: Boolean
        },
        successText: {
            default: null,
            type: String
        },
        dangerText: {
            default: null,
            type: String
        },
        warningText: {
            default: null,
            type: String
        },
        descriptionText: {
            default: null,
            type: String
        },
        size: {
            default: 'normal',
            type: String
        },
        valIconPack: {
            default: 'material-icons',
            type: String
        },
        valIconSuccess: {
            default: 'icon-check',
            type: String
        },
        valIconDanger: {
            default: 'icon-x',
            type: String
        },
        valIconWarning: {
            default: 'icon-alert-triangle',
            type: String
        },
        selectOnFocus: {
            type: Boolean,
            default: false
        },
        disableChromeAutocomplete: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isFocus: false
        }
    },
    computed: {
        style() {
            return {
                border: `1px solid ${
                    this.isFocus ? _color.getColor(this.color, 1) : 'rgba(0, 0, 0,.2)'
                }`
            }
        },
        styleLabel() {
            return {
                color: this.isFocus ? _color.getColor(this.color, 1) : null
            }
        },
        hasLabel() {
            return this.label || this.labelPlaceholder
        },
        isValue() {
            return this.labelPlaceholder ? true : !this.value
        },
        listeners() {
            return {
                ...this.$listeners,
                input: (evt) => {
                    this.$emit('input', evt.target.value)
                },
                focus: (evt) => {
                    if (this.disableChromeAutocomplete) {
                        // use dummy value: off
                        evt.target.setAttribute('autocomplete', 'some-stupid-value')
                    }
                    this.$emit('focus', evt)
                    this.changeFocus(true)
                    if (this.selectOnFocus) {
                        evt.target.select()
                    }
                },
                blur: (evt) => {
                    this.$emit('blur', evt)
                    this.changeFocus(false)
                }
            }
        }
    },
    methods: {
        // animation
        changeFocus(booleanx) {
            this.isFocus = booleanx
        },
        beforeEnter(el) {
            el.style.height = 0
        },
        enter(el, done) {
            let h = el.scrollHeight
            el.style.height = h + 'px'
            done()
        },
        leave: function (el) {
            el.style.height = 0 + 'px'
        },
        focusInput() {
            this.$refs.vsinput.focus()
        }
    }
}
</script>

<style scoped>
.validation-padding {
    height: 19px;
}

.vx-textarea * {
    transition: all 0.3s ease;
    /* height: 100%; */
}

.vx-textarea--input {
    padding-left: 43px;
    background-color: transparent;
    font-size: 14px;
    color: inherit;
    font-family: system-ui;
    line-height: 1.6;
    border-radius: 6px;
    border: 0 solid transparent;
    display: block;
    padding: 9px;
    font-size: 1rem !important;
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    background: transparent;
    resize: none;
    overflow: auto;
}

.vs-input--placeholder.static-label {
    color: rgba(0, 0, 0, 0.4) !important;
}

.vx-textarea.no-icon .vx-textarea--input {
    padding-left: 15px;
}

.vs-input--placeholder {
    left: 35px;
    top: 2px;
    font-size: 11.9px;
    transition: all 0.2s ease;
}

.vx-textarea.no-icon .vs-input--placeholder {
    left: 5px;
}

.vx-icon {
    margin: 5px 0;
    padding-left: 4px;
    padding-top: 5px;
    height: calc(100% - 25px);
}

.vx-textarea--input.hasValue + .vs-input--placeholder,
.vx-textarea--input:focus + .vs-input--placeholder {
    font-size: 9.8px;
    transform: translate(-40px, -90%) !important;
}

.vx-textarea--input.no-icon.hasValue + .vs-input--placeholder,
.vx-textarea--input.no-icon:focus + .vs-input--placeholder {
    transform: translate(-10px, -90%) !important;
}

.vx-textarea.isFocus .vs-input--label {
    color: rgba(var(--vs-primary), 1);
}

.vx-textarea.isFocus .vs-input--placeholder {
    color: rgba(var(--vs-primary), 1);
}

.vx-textarea.isFocus .vs-textarea-primary {
    border-color: rgba(var(--vs-primary), 1) !important;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
}
.vx-textarea .vs-con-textarea-danger {
    border-color: rgba(var(--vs-danger), 1) !important;
    box-shadow: 0;
}

.vx-textarea.isFocus i {
    color: rgba(var(--vs-primary), 1);
}
</style>
